export type GameTutorialEvent =
  | "word_completed"
  | "prefix_matched"
  | "ambiguous_prefix"
  | "danger_cleared"
  | "chain_cleared"
  | "ability_triggered"
  | "mini_wave_complete";

export interface GameTutorialStep {
  id:
    | "complete"
    | "prefix"
    | "ambiguity"
    | "danger"
    | "chain"
    | "ability"
    | "mini-wave";
  kicker: string;
  title: string;
  instruction: string;
  completionEvent: GameTutorialEvent;
  targetHint: string;
}

export interface GameTutorialProgress {
  stepIndex: number;
  completed: boolean;
  advanced: boolean;
}

export const GAME_TUTORIAL_STEPS: GameTutorialStep[] = [
  {
    id: "complete",
    kicker: "Lesson 1 of 7",
    title: "Clear one threat",
    instruction: "Type the complete visible word. Clean completions protect the Core.",
    completionEvent: "word_completed",
    targetHint: "Type focus",
  },
  {
    id: "prefix",
    kicker: "Lesson 2 of 7",
    title: "Use a prefix",
    instruction: "Begin typing steady. A unique prefix locks the target before the word is complete.",
    completionEvent: "prefix_matched",
    targetHint: "Begin steady",
  },
  {
    id: "ambiguity",
    kicker: "Lesson 3 of 7",
    title: "Resolve ambiguity",
    instruction: "Calm and cast share ca. Keep typing until one target remains. Escape clears your prefix.",
    completionEvent: "prefix_matched",
    targetHint: "Type cal",
  },
  {
    id: "danger",
    kicker: "Lesson 4 of 7",
    title: "Read the danger frame",
    instruction: "The marked threat is close to the Core. Clear it before lower-priority words.",
    completionEvent: "danger_cleared",
    targetHint: "Clear danger",
  },
  {
    id: "chain",
    kicker: "Lesson 5 of 7",
    title: "Break a word chain",
    instruction: "Linked words unlock in order. Clear the bright inner plate, then its partner.",
    completionEvent: "chain_cleared",
    targetHint: "Clear link, then guard",
  },
  {
    id: "ability",
    kicker: "Lesson 6 of 7",
    title: "Trigger a tactical effect",
    instruction: "Complete the charged word to trigger Word Freeze. Abilities never type for you.",
    completionEvent: "ability_triggered",
    targetHint: "Complete charge",
  },
  {
    id: "mini-wave",
    kicker: "Final lesson",
    title: "Complete the mini-wave",
    instruction: "Clear all three threats. Accuracy and readable priorities matter more than frantic input.",
    completionEvent: "mini_wave_complete",
    targetHint: "Clear the arena",
  },
];

export function getGameTutorialStep(
  stepIndex: number,
): GameTutorialStep | null {
  return GAME_TUTORIAL_STEPS[stepIndex] ?? null;
}

export function advanceGameTutorial(
  stepIndex: number,
  event: GameTutorialEvent,
): GameTutorialProgress {
  const step = getGameTutorialStep(stepIndex);
  if (!step || step.completionEvent !== event) {
    return {
      stepIndex,
      completed: stepIndex >= GAME_TUTORIAL_STEPS.length,
      advanced: false,
    };
  }
  const nextIndex = stepIndex + 1;
  return {
    stepIndex: nextIndex,
    completed: nextIndex >= GAME_TUTORIAL_STEPS.length,
    advanced: true,
  };
}
